import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { environmentState } from '~/state/atoms/engineAtoms';
import { useGetOrCreateDestination } from '~/components/toolbar/OperationsToolbar/shared/useGetOrCreateDestination';
import {
    applyComputedGeometry,
    type AppliedGeometry,
    type GeometryWrite,
} from './applyComputedGeometry';
import { recordContourEdit } from './recordContourEdit';

/**
 * Binds {@link applyComputedGeometry} to the live engine, the shared
 * destination-creation choreography, and the domain audit trail — one binding
 * every caller shares (the agent ops today, the toolbar next) so the
 * create/write/record tail has a single implementation.
 */
export function useApplyComputedGeometry(): (
    newRoiName: string,
    geometry: GeometryWrite
) => Promise<AppliedGeometry> {
    const env = useRecoilValue(environmentState);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment: env,
    });

    return useCallback(
        (newRoiName, geometry) =>
            applyComputedGeometry(newRoiName, geometry, {
                createDestinationRoi: async (name) =>
                    (await getOrCreateDestination(null, name)) ?? null,
                triggerContourLayerUpdate: () =>
                    env?.triggerContourLayerUpdate(),
                // Attribute the edit to the structure set the destination was
                // just created in — read live from the engine, not a value
                // captured at render that a structure-set switch could stale.
                recordContourEdit: (dest) =>
                    recordContourEdit(env?.structureSet?.sopInstanceUID, dest),
            }),
        [env, getOrCreateDestination]
    );
}
